<template>
  <div class="sp-page">
    <div class="sp-header">
      <div style="font-size:22px;font-weight:700;">SimiTrac · Sponsor Portal</div>
      <div v-if="data" style="font-size:13px;opacity:.85;">{{ data.institution_name }}</div>
    </div>

    <div v-if="loading && !data" class="sp-center"><div class="spinner-border"></div></div>
    <div v-else-if="error" class="sp-center sp-error">{{ error }}</div>
    <div v-else-if="data" class="sp-body">
      <div class="sp-section">
        <div style="font-size:20px;font-weight:700;color:#0D2B5E;">{{ data.sponsor.name }}</div>
        <div style="font-size:12px;color:#666;">{{ data.sponsor.type === 'ngo' ? 'NGO' : 'Individual Sponsor' }} · {{ data.sponsor.contact_person || '' }}</div>
      </div>

      <div class="sp-grid">
        <div class="sp-card">
          <div class="sp-card-lbl">Active Students</div>
          <div class="sp-card-val">{{ data.active_students }}</div>
        </div>
        <div class="sp-card" style="border-left-color:#C62828;">
          <div class="sp-card-lbl">Outstanding</div>
          <div class="sp-card-val" style="color:#C62828;">KSh {{ fmt(data.total_outstanding) }}</div>
        </div>
        <div class="sp-card" style="border-left-color:#1B5E20;">
          <div class="sp-card-lbl">Settled In Period</div>
          <div class="sp-card-val" style="color:#1B5E20;">KSh {{ fmt(data.period_settled) }}</div>
        </div>
        <div class="sp-card" style="border-left-color:#F59E0B;">
          <div class="sp-card-lbl">Period Spend</div>
          <div class="sp-card-val" style="color:#F59E0B;">KSh {{ fmt(data.period_debits) }}</div>
        </div>
      </div>

      <div class="sp-section">
        <div class="d-flex gap-2 flex-wrap align-items-end justify-content-between">
          <div class="d-flex gap-2 flex-wrap align-items-end">
            <div><label class="sp-lbl">From</label><input type="date" class="sp-input" v-model="dateFrom" /></div>
            <div><label class="sp-lbl">To</label><input type="date" class="sp-input" v-model="dateTo" /></div>
            <button class="sp-btn" @click="load">Apply</button>
          </div>
          <button class="sp-btn sp-btn-pay" @click="settleOpen = true" :disabled="!data.total_outstanding">💵 Settle Outstanding</button>
        </div>
      </div>

      <!-- Settle via STK modal -->
      <div v-if="settleOpen" class="sp-overlay" @click.self="settleOpen = false">
        <div class="sp-modal">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h3 style="margin:0;color:#0D2B5E;font-size:16px;">Settle via M-Pesa</h3>
            <button class="sp-x" @click="settleOpen = false">✕</button>
          </div>
          <div style="font-size:12px;color:#666;margin-bottom:10px;">
            Current outstanding: <strong style="color:#C62828;">KSh {{ fmt(data.total_outstanding) }}</strong> across {{ data.active_students }} student(s).
            Your payment will be split proportionally.
          </div>

          <div class="row" style="display:flex;gap:8px;">
            <div style="flex:1;">
              <label class="sp-lbl">Amount (KES)</label>
              <input type="number" class="sp-input" v-model.number="settleForm.amount" min="10" />
            </div>
            <div style="flex:1;">
              <label class="sp-lbl">Your M-Pesa Phone</label>
              <input class="sp-input" v-model="settleForm.phone" placeholder="07…" />
            </div>
          </div>

          <div class="d-flex gap-2 mt-3" style="display:flex;gap:8px;margin-top:12px;">
            <button class="sp-btn sp-btn-pay" @click="initiateStk" :disabled="!canInitiate || stkBusy">
              <span v-if="stkBusy">Sending…</span>
              <span v-else>Send M-Pesa Prompt</span>
            </button>
            <button class="sp-btn" style="background:#888;" @click="settleOpen = false">Cancel</button>
          </div>

          <div v-if="stkMsg" class="mt-3" style="margin-top:14px;font-size:13px;padding:10px;border-radius:6px;"
               :style="{background: stkOk ? '#E8F5E9' : '#FFEBEE', color: stkOk ? '#1B5E20' : '#C62828'}">
            {{ stkMsg }}
          </div>
          <div v-if="stkPending" style="margin-top:10px;font-size:12px;color:#666;">
            ⏳ Checking status… <button class="sp-x" style="text-decoration:underline;color:#0D2B5E;font-size:12px;padding:0;border:0;background:none;cursor:pointer;" @click="pollStkStatus">refresh</button>
          </div>
        </div>
      </div>

      <div class="sp-section">
        <h3>Sponsored Students</h3>
        <table class="sp-table">
          <thead><tr><th>Student</th><th>Admission</th><th style="text-align:right;">Outstanding</th><th>Status</th></tr></thead>
          <tbody>
            <tr v-for="s in data.sponsorships" :key="s.id">
              <td>{{ s.student_name }}</td>
              <td>{{ s.admission_no }}</td>
              <td style="text-align:right;" :style="{color: s.outstanding > 0 ? '#C62828' : '#444', fontWeight:600}">KSh {{ fmt(s.outstanding) }}</td>
              <td>{{ Number(s.active) === 1 ? 'Active' : 'Ended' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="sp-section">
        <h3>Settlements ({{ data.settlements.length }})</h3>
        <table class="sp-table">
          <thead><tr><th>Date</th><th>Method</th><th>M-Pesa</th><th>Batch</th><th style="text-align:right;">Amount</th></tr></thead>
          <tbody>
            <tr v-for="s in data.settlements" :key="s.id">
              <td>{{ s.paid_on }}</td>
              <td>{{ s.payment_method }}</td>
              <td style="font-family:monospace;font-size:12px;">{{ s.mpesa_code || '—' }}</td>
              <td style="font-family:monospace;font-size:11px;color:#555;">{{ s.batch_ref || '—' }}</td>
              <td style="text-align:right;color:#1B5E20;font-weight:600;">KSh {{ fmt(s.amount) }}</td>
            </tr>
            <tr v-if="!data.settlements.length"><td colspan="5" style="text-align:center;color:#888;padding:14px;">No settlements in this period.</td></tr>
          </tbody>
        </table>
      </div>

      <div class="sp-section">
        <h3>Wallet Activity ({{ data.activity.length }})</h3>
        <div style="overflow-x:auto;">
          <table class="sp-table">
            <thead><tr><th>When</th><th>Student</th><th>Category</th><th>Type</th><th style="text-align:right;">Amount</th><th style="text-align:right;">Balance</th></tr></thead>
            <tbody>
              <tr v-for="(t, i) in data.activity" :key="i">
                <td>{{ (t.created_at || '').slice(0, 16) }}</td>
                <td>{{ t.student_name }}</td>
                <td>{{ t.category }}</td>
                <td>{{ t.type }}</td>
                <td style="text-align:right;" :style="{color: t.type === 'credit' ? '#1B5E20' : '#C62828', fontWeight:600}">
                  {{ t.type === 'credit' ? '+' : '−' }} KSh {{ fmt(t.amount) }}
                </td>
                <td style="text-align:right;" :style="{color: Number(t.balance_after) < 0 ? '#C62828' : '#555'}">KSh {{ fmt(t.balance_after) }}</td>
              </tr>
              <tr v-if="!data.activity.length"><td colspan="6" style="text-align:center;color:#888;padding:14px;">No activity in this period.</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="sp-footer">Read-only sponsor portal · Powered by SimiTrac</div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import axios from "axios";
import { simitrack } from "@/api";
export default {
  name: 'SponsorPortal',
  data() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      loading: false,
      error: '',
      data: null,
      dateFrom: today.slice(0, 8) + '01',
      dateTo: today,
      settleOpen: false,
      settleForm: { amount: 0, phone: '' },
      stkBusy: false, stkMsg: '', stkOk: false,
      stkPending: false, stkCheckoutId: '',
      stkPoller: null,
    };
  },
  computed: {
    canInitiate() {
      return Number(this.settleForm.amount) >= 10 && (this.settleForm.phone || '').length >= 9;
    },
  },
  mounted() { this.load(); },
  methods: {
    fmt(n) { return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    async load() {
      const token = this.$route.params.token;
      if (!token) { this.error = "Missing portal token"; return; }
      this.loading = true;
      try {
        const res = await axios.post(simitrack, { function: 'getSponsorPortalData', token, dateFrom: this.dateFrom, dateTo: this.dateTo });
        if (res.data.success) {
          this.data = res.data.data;
          this.error = '';
          if (this.settleForm.amount === 0 && Number(this.data.total_outstanding) > 0) {
            this.settleForm.amount = Math.round(this.data.total_outstanding);
          }
        } else {
          this.error = res.data.message || 'Failed to load portal data';
        }
      } catch (e) {
        this.error = e.response?.data?.message || e.message;
      }
      this.loading = false;
    },
    async initiateStk() {
      const token = this.$route.params.token;
      this.stkBusy = true; this.stkMsg = ''; this.stkOk = false;
      try {
        const res = await axios.post(simitrack, {
          function: 'initiateSponsorSettlementStk', token,
          amount: this.settleForm.amount, phone: this.settleForm.phone,
        });
        if (res.data.success) {
          this.stkOk = true;
          this.stkMsg = res.data.message;
          this.stkCheckoutId = res.data.data.checkout_request_id;
          this.stkPending = true;
          this.startStkPoll();
        } else {
          this.stkMsg = res.data.message || 'STK push failed';
        }
      } catch (e) {
        this.stkMsg = e.response?.data?.message || e.message;
      }
      this.stkBusy = false;
    },
    startStkPoll() {
      if (this.stkPoller) clearInterval(this.stkPoller);
      let ticks = 0;
      this.stkPoller = setInterval(() => {
        ticks++;
        if (ticks > 30) { clearInterval(this.stkPoller); this.stkPoller = null; this.stkPending = false; return; }
        this.pollStkStatus();
      }, 4000);
    },
    async pollStkStatus() {
      if (!this.stkCheckoutId) return;
      const token = this.$route.params.token;
      try {
        const res = await axios.post(simitrack, {
          function: 'checkSponsorSettlementStkStatus', token, checkoutRequestId: this.stkCheckoutId,
        });
        if (res.data.success) {
          const s = res.data.data;
          if (s.status === 'completed') {
            this.stkOk = true;
            this.stkMsg = `✅ Payment of KSh ${this.fmt(s.amount)} received. Receipt: ${s.provider_ref || ''}`;
            this.stkPending = false;
            if (this.stkPoller) { clearInterval(this.stkPoller); this.stkPoller = null; }
            setTimeout(() => { this.settleOpen = false; this.load(); }, 1500);
          } else if (s.status === 'failed' || s.status === 'cancelled') {
            this.stkOk = false;
            this.stkMsg = `❌ ${s.status.toUpperCase()}: ${s.result_desc || ''}`;
            this.stkPending = false;
            if (this.stkPoller) { clearInterval(this.stkPoller); this.stkPoller = null; }
          }
        }
      } catch (e) {}
    },
  },
  beforeUnmount() { if (this.stkPoller) clearInterval(this.stkPoller); },
};
</script>

<style scoped>
.sp-page{min-height:100vh;background:#F5F8FC;color:#222;font-family:'Inter', Arial, sans-serif;}
.sp-header{background:#0D2B5E;color:#fff;padding:18px 24px;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px;}
.sp-body{max-width:1200px;margin:18px auto;padding:0 16px;}
.sp-section{background:#fff;border:1px solid #E0E6ED;border-radius:10px;padding:14px 18px;margin-bottom:14px;}
.sp-section h3{font-size:14px;font-weight:700;color:#0D2B5E;margin:0 0 10px;text-transform:uppercase;letter-spacing:.5px;}
.sp-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:10px;margin-bottom:14px;}
.sp-card{background:#fff;border:1px solid #E0E6ED;border-radius:10px;padding:14px;border-left:3px solid #0D2B5E;}
.sp-card-lbl{font-size:10px;color:#666;text-transform:uppercase;font-weight:600;letter-spacing:.5px;}
.sp-card-val{font-size:22px;font-weight:800;margin-top:4px;color:#0D2B5E;}
.sp-lbl{font-size:11px;color:#666;font-weight:600;display:block;margin-bottom:3px;}
.sp-input{background:#F5F8FC;border:1px solid #D6DEE8;border-radius:6px;padding:7px 10px;font-size:13px;outline:none;color:#222;}
.sp-input:focus{border-color:#0D2B5E;}
.sp-btn{background:#0D2B5E;color:#fff;border:none;font-weight:600;border-radius:6px;padding:8px 16px;font-size:13px;cursor:pointer;}
.sp-table{width:100%;border-collapse:collapse;font-size:13px;}
.sp-table thead tr{background:#F0F4FA;}
.sp-table th{padding:8px 10px;color:#0D2B5E;font-size:10px;font-weight:700;text-transform:uppercase;border-bottom:1px solid #D6DEE8;text-align:left;}
.sp-table td{padding:7px 10px;border-bottom:1px solid #ECEFF4;color:#333;}
.sp-table tr:hover{background:#FAFCFE;}
.sp-center{display:flex;justify-content:center;align-items:center;padding:60px 20px;color:#666;font-size:14px;}
.sp-error{color:#C62828;}
.sp-footer{text-align:center;font-size:11px;color:#888;padding:18px 0;}
.sp-btn-pay{background:#10B981;}
.sp-btn-pay:disabled{background:#94A3B8;cursor:not-allowed;}
.sp-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;z-index:99;padding:20px;}
.sp-modal{background:#fff;border-radius:10px;padding:20px;max-width:480px;width:100%;box-shadow:0 12px 40px rgba(0,0,0,.2);}
.sp-x{background:none;border:none;color:#666;font-size:18px;cursor:pointer;padding:0 6px;}
</style>
